import type { SupportAttachmentType } from "@repo/shared/types";
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

type SidebarTab =
  | "docs"
  | "chat"
  | "client-chat"
  | "system-ai"
  | "scheduler"
  | "ideas"
  | "support"
  | "ai-settings"
  | null;

export type AiSettingsSection = "prompts" | "playground" | "mcp" | "imap" | "usage";

export type SupportContext = {
  entityType: SupportAttachmentType;
  entityId: string;
  displayName: string;
};

interface SidebarContextType {
  activeTab: SidebarTab;
  isOpen: boolean;
  width: number;
  setWidth: (width: number) => void;
  openTab: (tab: SidebarTab) => void;
  closeTab: () => void;
  toggleTab: (tab: SidebarTab) => void;
  openDocPage: (pageIdOrSlug: string) => void;
  openAiSettings: (section?: AiSettingsSection) => void;
  openAiAgentThread: (threadId: string) => void;
  /** QL-728 — open the support panel on one ticket (a support-reply notification). */
  openSupportTicket: (ticketId: string) => void;
  supportContext: SupportContext | null;
  setSupportContext: (ctx: SupportContext | null) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [activeTab, setActiveTab] = useState<SidebarTab>(null);
  const [width, setWidth] = useState(480);
  const [supportContext, setSupportContext] = useState<SupportContext | null>(null);

  const isOpen = activeTab !== null;

  const openTab = useCallback((tab: SidebarTab) => {
    setActiveTab(tab);
  }, []);

  const closeTab = useCallback(() => {
    setActiveTab(null);
  }, []);

  const toggleTab = useCallback((tab: SidebarTab) => {
    setActiveTab((current) => (current === tab ? null : tab));
  }, []);

  const openDocPage = useCallback((pageIdOrSlug: string) => {
    // Always open docs tab (no toggle behavior)
    setActiveTab("docs");

    // Dispatch event to navigate to specific page
    setTimeout(() => {
      const event = new CustomEvent("navigate-to-doc", {
        detail: { pageId: pageIdOrSlug, title: "" },
      });
      window.dispatchEvent(event);
    }, 100); // Small delay to ensure panel is mounted
  }, []);

  const openAiSettings = useCallback((section?: AiSettingsSection) => {
    setActiveTab("ai-settings");
    if (section) {
      setTimeout(() => {
        const event = new CustomEvent("navigate-to-ai-settings", {
          detail: { section },
        });
        window.dispatchEvent(event);
      }, 100);
    }
  }, []);

  const openAiAgentThread = useCallback((threadId: string) => {
    setActiveTab("chat");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("open-ai-agent-thread", { detail: { threadId } }));
    }, 100);
  }, []);

  const openSupportTicket = useCallback((ticketId: string) => {
    setActiveTab("support");
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("support:open-ticket", { detail: { ticketId } }));
    }, 100);
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({
      activeTab,
      isOpen,
      width,
      setWidth,
      openTab,
      closeTab,
      toggleTab,
      openDocPage,
      openAiSettings,
      openAiAgentThread,
      openSupportTicket,
      supportContext,
      setSupportContext,
    }),
    [
      activeTab,
      isOpen,
      width,
      openTab,
      closeTab,
      toggleTab,
      openDocPage,
      openAiSettings,
      openAiAgentThread,
      openSupportTicket,
      supportContext,
    ],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return context;
}

// Safe version that returns null instead of throwing when outside provider
export function useSidebarSafe() {
  return useContext(SidebarContext);
}
